import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Spinner, Collapse } from "react-bootstrap";
import { Breadcrumb, Card, Alert, ListGroup } from "react-bootstrap";
import modalStyles from "../modal-styles";
import { downloadAnalysis, getCompetitorMarketAnalysis } from '../../../service/workflow-api';
import { toast } from 'react-toastify';


// Function to convert Pascal case to normal spaced text
const pascalToNormal = (text) => {
  return text.replace(/([A-Z])/g, ' $1').trim();
};

const SavedItems = ({ onDelete, onLoad, onView, onBack }) => {
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    setLoading(true);

    // Actual API call to fetch saved items
    const fetchSavedItems = async () => {
      try {
        const response = await getCompetitorMarketAnalysis();
        setSavedItems(response);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching saved items:", error);
        setLoading(false);
      }
    };

    fetchSavedItems();
  }, []);

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleDownloadReport = async (analysisId) => {
    try {
      const response = await downloadAnalysis(analysisId);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const htmlContent = data.html;
  
      // Create a new window with a meaningful title and URL
      const newWindow = window.open(' ', '_blank');
      
      if (newWindow) {
        newWindow.document.title = `Analysis Report ${analysisId}`;
        
        newWindow.document.open();
        newWindow.document.write(htmlContent);
        
        const printStyles = `
          <style type="text/css">
            @media print {
              .print-button {
                display: none !important;
              }
              @page {
                size: auto;
                margin: 20mm 10mm 20mm 10mm;
              }
            }
          </style>
        `;
        newWindow.document.head.insertAdjacentHTML('beforeend', printStyles);
        newWindow.document.close();
        
        newWindow.addEventListener('load', () => {
          const printButton = newWindow.document.createElement('button');
          printButton.innerHTML = 'Print Report';
          printButton.className = 'print-button';
          printButton.style.cssText = 'position: fixed; top: 20px; right: 20px; padding: 8px 16px; background-color: #6e2ae8; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;';
          printButton.addEventListener('click', () => {
            newWindow.print();
          });
          newWindow.document.body.appendChild(printButton);
        });
      } else {
        toast.warning("Popup blocked. Please allow popups for this site to view the report.");
      }
    } catch (error) {
      console.error("Error downloading analysis:", error);
      toast.error("Failed to download analysis.");
    }
  };

  // Filter attributes with non-empty text values
  const getAttributesWithText = (formData) => {
    const result = {};
    if (!formData) return result;
    
    Object.keys(formData).forEach(key => {
      if (formData[key] && formData[key].trim()) {
        result[key] = formData[key];
      }
    });
    
    return result;
  };

  return (
    <>
      <Breadcrumb className="dark-breadcrumb mb-4" style={{ color: "white" }}>
        <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
        <Breadcrumb.Item href="/ai-workflows/ai-comp-market-research">Competitor and Market Research</Breadcrumb.Item>
        <Breadcrumb.Item active>Saved Items</Breadcrumb.Item>
      </Breadcrumb>

      <Container fluid className="budget-planner-container py-4 min-vh-100">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card className="dark-main-card text-light mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title as="h2" style={{ fontWeight: 400, color: '#d9d9d9' }}>
                    Saved Research Items
                  </Card.Title>
                  <Button 
                    variant="outline-primary"
                    className="dark-outline-button"
                    onClick={onBack}
                  >
                    Back to Research Tool
                  </Button>
                </div>
                {
                  loading && (
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  )
                }
                {!loading && savedItems.length === 0 ? (
                  <Alert variant="info" className="dark-alert">
                    No saved research items found. Perform a research to see it here.
                  </Alert>
                ) : (
                  <ListGroup variant="flush" className="dark-list-group">
                    {savedItems.map((item, i) => {
                      const attributesWithText = getAttributesWithText(item.form_data_json);
                      const isExpanded = expandedItems[item.id] || false;
                      
                      return (
                        <ListGroup.Item 
                          key={item.id} 
                          className="dark-list-item mb-3 p-3"
                          style={modalStyles.darkListItem}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h5 className="mb-1 text-primary">{item.name || `Research #${(savedItems.length - i)}`}</h5>
                              <p className="mb-1 small" style={{ color: '#ffffff' }}>
                                Created: {new Date(item.created_at).toLocaleString()}
                              </p>
                              
                              {Object.keys(attributesWithText).length > 0 && (
                                <Button 
                                  variant="link" 
                                  onClick={() => toggleExpand(item.id)}
                                  className="p-0 text-light"
                                  style={{ textDecoration: 'none' }}
                                >
                                  {isExpanded ? 'Hide Details' : 'Show Details'} ({Object.keys(attributesWithText).length} items)
                                </Button>
                              )}
                            </div>
                            <div>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="dark-outline-button"
                                onClick={() => handleDownloadReport(item.id)}
                              >
                                View or Print
                              </Button>
                            </div>
                          </div>
                          
                          <Collapse in={isExpanded}>
                            <div className="mt-3">
                              <Card className="dark-card">
                                <Card.Body>
                                  {Object.entries(attributesWithText).map(([key, value]) => (
                                    <div key={key} className="mb-3">
                                      <h6 className="text-primary">{pascalToNormal(key)}</h6>
                                      <p className="small" style={{ color: '#d9d9d9' }}>{value}</p>
                                    </div>
                                  ))}
                                </Card.Body>
                              </Card>
                            </div>
                          </Collapse>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
                <br/>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SavedItems;
